/**
 * CORP//LLM i18n — Locale loader and translation utility.
 *
 * Locale source: corpllm-i18n GitHub repo, published to R2 via CI as
 * https://assets.corpllm.io/locales/{lang}.json (no auth, 5-min edge cache).
 * The client fetches once on boot and on language switch. Updates from
 * the i18n repo propagate worldwide within 5 minutes without a client redeploy.
 *
 * Usage:
 *   import { t, loadI18n, setLang, getLang } from './i18n';
 *
 *   await loadI18n();                    // call once in boot()
 *   t('dashboard.income')               // → "Einnahmen" (de) or "Income" (en)
 *   t('dashboard.title', { cycle: 3 }) // → "Dashboard · Zyklus 3"
 */

import { CDN_BASE } from './data/cdn';

/** Flat key→value catalog currently in use. */
let catalog: Record<string, string> = {};

/** Active language code (BCP-47 short form). */
let currentLang = 'de';

/** Supported languages — extend when adding new locales. */
export const SUPPORTED_LANGS = ['de', 'en'] as const;
export type SupportedLang = (typeof SUPPORTED_LANGS)[number];

// ── Public API ────────────────────────────────────────────────────────────

/** Returns the active language code. */
export function getLang(): string {
  return currentLang;
}

/**
 * Sets the active language and persists to localStorage.
 * Caller is responsible for re-rendering (e.g. window.location.reload()).
 */
export function setLang(lang: string): void {
  currentLang = lang;
  try { localStorage.setItem('lang', lang); } catch { /* SSR / test */ }
}

/**
 * Pre-loads a catalog directly — used in unit tests to avoid fetch().
 */
export function initI18n(data: Record<string, string>): void {
  catalog = data;
}

/**
 * Loads the locale JSON file for the given (or stored) language.
 * Falls back silently to empty catalog if fetch fails (keys become fallback).
 */
export async function loadI18n(lang?: string): Promise<void> {
  let stored = 'de';
  try { stored = localStorage.getItem('lang') ?? 'de'; } catch { /* SSR */ }
  currentLang = lang ?? stored;
  try {
    // Locales come from corpllm-i18n via R2 (managed in a separate repo so
    // they can be community-edited without touching the client). Public
    // endpoint, no auth, 5-min edge cache. Falls back silently to an empty
    // catalog if fetch fails — t() then returns the raw key.
    const res = await fetch(`${CDN_BASE}/locales/${currentLang}.json`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    catalog = await res.json() as Record<string, string>;
  } catch {
    // graceful fallback — t() will return keys
    catalog = {};
  }
}

/**
 * Translates a key, optionally interpolating `{varName}` placeholders.
 * Returns the key itself if no translation is found.
 *
 * @example
 *   t('dashboard.title', { cycle: 3 }) // "Dashboard · Zyklus 3"
 */
export function t(key: string, vars?: Record<string, string | number>): string {
  let val = catalog[key] ?? key;
  if (vars) {
    for (const [k, v] of Object.entries(vars)) {
      val = val.split(`{${k}}`).join(String(v)); // replaceAll via split/join
    }
  }
  return val;
}

/**
 * Updates all DOM elements with a `data-i18n` attribute to their translated text.
 * Call after loadI18n() resolves and the DOM is ready.
 *
 * @example  <span data-i18n="topbar.cycle">Zyklus</span>
 */
export function applyI18nToDOM(): void {
  if (Object.keys(catalog).length === 0) return; // locale not loaded — keep HTML fallback text
  document.querySelectorAll<HTMLElement>('[data-i18n]').forEach(el => {
    const key = el.dataset.i18n!;
    el.textContent = t(key);
  });
  // Update <html lang="...">
  document.documentElement.lang = currentLang;
}
